import { createContext } from 'lib/createContext'
import React, { PropsWithChildren, useState } from 'react'

type Workflow<Args extends Record<string, unknown> | undefined = undefined> = {
  open: Args extends undefined ? () => void : (args: Args) => void
  close: () => void
  isOpen: boolean
} & (Args extends undefined ? object : { data?: Args })

type WorkflowState<T extends Record<string, unknown> | undefined = undefined> =
  T extends undefined
    ? boolean
    : {
        isOpen: boolean
        data?: T
      }

export type ShiftDetailWorkflowsContextState = {
  assignShiftFlow: Workflow<{
    shiftId: number
  }>
  switchTiersFlow: Workflow<{
    listingId: number
    shiftId: number
  }>
  createCustomShiftNotificationFlow: Workflow
}

const [ShiftDetailWorkflowsContext, useShiftDetailWorkflowsContextValue] =
  createContext<ShiftDetailWorkflowsContextState>({
    name: 'ShiftDetailWorkflowsContext',
  })

type ShiftDetailWorkflowsProviderProps = PropsWithChildren

export const ShiftDetailWorkflowsProvider = ({
  children,
}: ShiftDetailWorkflowsProviderProps) => {
  const [assignShiftFlow, setAssignShiftFlow] = useState<
    WorkflowState<{
      shiftId: number
    }>
  >({ isOpen: false, data: undefined })
  const [switchTiersFlow, setSwitchTiersFlow] = useState<
    WorkflowState<{
      listingId: number
      shiftId: number
    }>
  >({ isOpen: false, data: undefined })
  const [
    isCreateCustomShiftNotificationFlowOpen,
    setIsCreateCustomShiftNotificationFlowOpen,
  ] = useState(false)

  const value: ShiftDetailWorkflowsContextState = {
    assignShiftFlow: {
      open: (data) => setAssignShiftFlow({ isOpen: true, data }),
      close: () => setAssignShiftFlow({ isOpen: false, data: undefined }),
      isOpen: assignShiftFlow.isOpen,
      data: assignShiftFlow.data,
    },
    switchTiersFlow: {
      open: (data) => setSwitchTiersFlow({ isOpen: true, data }),
      close: () => setSwitchTiersFlow({ isOpen: false, data: undefined }),
      isOpen: switchTiersFlow.isOpen,
      data: switchTiersFlow.data,
    },
    createCustomShiftNotificationFlow: {
      open: () => setIsCreateCustomShiftNotificationFlowOpen(true),
      close: () => setIsCreateCustomShiftNotificationFlowOpen(false),
      isOpen: isCreateCustomShiftNotificationFlowOpen,
    },
  }

  return (
    <ShiftDetailWorkflowsContext.Provider value={value}>
      {children}
    </ShiftDetailWorkflowsContext.Provider>
  )
}

export { useShiftDetailWorkflowsContextValue }
